/**
 * Les noms des échéances, en français et en anglais.
 *
 * Sortis du serveur MCP (lib/tools/mcpServer.js), qui les écrivait pour les
 * assistants, pour que la page « Votre situation » dise exactement les mêmes
 * mots sans charger tout le serveur dans le navigateur.
 *
 * Ils reprennent la frise de l'accueil (micro) et l'outil « Les échéances de
 * votre société » (CompanyDeadlines.jsx) : un libellé changé là-bas se change
 * ici aussi.
 */
export const LIBELLES_ECHEANCES = {
    micro: {
        fr: {
            "acre-demande": "Dernier jour pour demander l'ACRE à l'Urssaf",
            "vfl-option": "Dernier jour pour opter pour le versement libératoire",
            "acre-fin": "Fin de l'ACRE : les cotisations repassent au taux plein",
            "cfe-declaration": "Déclaration initiale de CFE, formulaire 1447-C",
            "cfe-premier-solde": "Première CFE à payer (l'année de création en est exonérée)",
        },
        en: {
            "acre-demande": "Last day to apply to the Urssaf for the ACRE",
            "vfl-option": "Last day to opt for the flat-rate income tax payment",
            "acre-fin": "End of the ACRE: contributions return to the full rate",
            "cfe-declaration": "Initial CFE return, form 1447-C",
            "cfe-premier-solde": "First CFE to pay (the year of creation is exempt)",
        },
    },
    nonCouvert: {
        fr: [
            "les déclarations de chiffre d'affaires à l'Urssaf, mensuelles ou trimestrielles selon le rythme choisi à l'immatriculation",
            "la déclaration annuelle de revenus, dont les dates changent chaque printemps",
        ],
        en: [
            "turnover returns to the Urssaf, monthly or quarterly depending on the pace chosen at registration",
            "the annual income tax return, whose dates change every spring",
        ],
    },
    societe: {
        fr: {
            ponctuelles: {
                liasse: "Liasse fiscale de l'exercice clos",
                solde: "Solde de l'impôt sur les sociétés",
                approbation: "Approbation des comptes (six mois après la clôture)",
                depot: "Dépôt des comptes au greffe",
                dividendesLimite: "Dernier jour pour verser les dividendes (neuf mois après la clôture)",
                acompteIs: "Acompte d'impôt sur les sociétés (un quart de l'impôt du dernier exercice)",
                cfe: "Solde de la cotisation foncière des entreprises",
                cfeDeclaration: "Déclaration initiale de CFE, formulaire 1447-C",
                tvaAcompteDecembre: "Acompte de TVA de décembre (40 % de la TVA de l'an dernier)",
                ifu: "Déclaration des dividendes versés l'an dernier (imprimé fiscal unique)",
                dispense: "Dispense d'acompte sur les dividendes de l'an prochain",
            },
            precisions: {
                liasse: "en ligne, quinze jours de plus que la date légale",
                incertain: "seulement si l'impôt du dernier exercice dépassait 3 000 €",
                fourchette: "entre le 15 et le 24, à la date indiquée dans l'espace professionnel",
                calculee: "date calculée selon la règle : le calendrier fiscal de cette année n'est pas encore paru",
            },
            recurrentes: {
                dsn: "Chaque 15 du mois : la déclaration sociale du salaire et ses cotisations.",
                cotisationsMois: "Chaque {jour} du mois : les cotisations Urssaf du gérant.",
                cotisationsTrimestre: "Les 5 février, 5 mai, 5 août et 5 novembre : les cotisations Urssaf du gérant.",
                ca3: "Chaque mois, entre le 15 et le 24 : la déclaration de TVA.",
                formulaire2777: "Le 15 du mois qui suit chaque versement de dividendes : le formulaire 2777.",
            },
            informations: {
                premierExercice: "Pendant le premier exercice, aucun acompte d'impôt sur les sociétés.",
                tva2027: "Le régime simplifié de TVA disparaît le 1er janvier 2027 ; les nouvelles dates n'étaient pas publiées au 24 septembre 2026.",
                tvaFranchise: "En franchise de TVA : aucune déclaration ; surveiller le seuil de 37 500 € en services, 85 000 € en vente.",
                declarationRevenus: "Au printemps, la rémunération de gérant se déclare avec les revenus : c'est elle qui ajuste les cotisations.",
                sansSalaire: "Sans salaire, aucune cotisation et aucune déclaration sociale.",
            },
        },
        en: {
            ponctuelles: {
                liasse: "Corporate tax return for the closed financial year",
                solde: "Corporate tax balance",
                approbation: "Approval of the accounts (six months after year end)",
                depot: "Filing of the accounts with the commercial court registry",
                dividendesLimite: "Last day to pay dividends (nine months after year end)",
                acompteIs: "Corporate tax instalment (a quarter of the last year's tax)",
                cfe: "Business property contribution (CFE) balance",
                cfeDeclaration: "Initial CFE return, form 1447-C",
                tvaAcompteDecembre: "December VAT instalment (40% of last year's VAT)",
                ifu: "Return of dividends paid last year (IFU)",
                dispense: "Exemption from the instalment on next year's dividends",
            },
            precisions: {
                liasse: "online, fifteen days more than the legal date",
                incertain: "only if the last financial year's tax exceeded €3,000",
                fourchette: "between the 15th and the 24th, on the date shown in the business account",
                calculee: "date worked out from the rule: this year's tax calendar has not been published yet",
            },
            recurrentes: {
                dsn: "Every 15th of the month: the social return for the salary and its contributions.",
                cotisationsMois: "Every {jour}th of the month: the manager's Urssaf contributions.",
                cotisationsTrimestre: "5 February, 5 May, 5 August and 5 November: the manager's Urssaf contributions.",
                ca3: "Every month, between the 15th and the 24th: the VAT return.",
                formulaire2777: "The 15th of the month after each dividend payment: form 2777.",
            },
            informations: {
                premierExercice: "During the first financial year, no corporate tax instalment.",
                tva2027: "The simplified VAT regime disappears on 1 January 2027; the new dates had not been published as of 24 September 2026.",
                tvaFranchise: "Under the VAT exemption: no return; watch the €37,500 threshold for services, €85,000 for sales.",
                declarationRevenus: "In spring, the manager's pay is declared with the income tax return: that is what adjusts the contributions.",
                sansSalaire: "Without a salary, no contributions and no social return.",
            },
        },
    },
};
